import React, { useEffect, useMemo } from 'react'
import { filter, find, isEmpty, isNil, map, noop, some, toString } from 'lodash'
import PropTypes from 'prop-types'

import { showErrorNotification } from '../../../core/helpers'
import { areSameValue, formatDateUk } from '../../../core/utils'

import { Select } from '../../../common/components/sections'
import { ALL_SELECT_FILTER_ITEM } from '../../../common/constants'
import { useUsersQuery } from '../../../common/hooks'

import {
  useDishCurationDatesLazyQuery,
  useDishesAvailableForQaCountsLazyQuery,
} from '../../../dishes/hooks'

const MenuCurationTaskCuratorFilter = ({
  menuId,
  selectedCurator,
  isDisabled,
  onChange,
  wasDoneInPreviousTwoWeeks,
}) => {
  const { data: usersData } = useUsersQuery()

  const [
    fetchCounts,
    { data: availableForQaCounts, error: availableForQaCountsError },
  ] = useDishesAvailableForQaCountsLazyQuery({ wasDoneInPreviousTwoWeeks })

  const [
    fetchCurationDates,
    { data: dishCurationDates, error: dishCurationDatesError },
  ] = useDishCurationDatesLazyQuery()

  useEffect(() => {
    if (availableForQaCountsError || dishCurationDatesError) {
      showErrorNotification({
        title: 'Failed to get required filter data',
        description: availableForQaCountsError,
      })
    }
  }, [availableForQaCountsError, dishCurationDatesError])

  useEffect(() => {
    if (!isDisabled) {
      fetchCounts(menuId)
    }
  }, [fetchCounts, isDisabled, menuId])

  useEffect(() => {
    if (isDisabled && !isNil(selectedCurator)) {
      onChange(null)
    }
  }, [isDisabled, onChange, selectedCurator])

  useEffect(() => {
    if (!isDisabled && !isEmpty(availableForQaCounts) && !isNil(menuId)) {
      const curatorIds = map(availableForQaCounts, 'userId')
      fetchCurationDates(menuId, curatorIds)
    }
  }, [availableForQaCounts, fetchCurationDates, isDisabled, menuId])

  const usersOptions = useMemo(
    () => [
      ALL_SELECT_FILTER_ITEM,
      ...map(
        filter(usersData, user =>
          some(availableForQaCounts, ({ userId }) =>
            areSameValue(userId, user.id),
          ),
        ),
        ({ id, name }) => {
          const dishCount = find(availableForQaCounts, ({ userId }) =>
            areSameValue(userId, id),
          )
          const curationDate = find(dishCurationDates, ({ userId }) =>
            areSameValue(userId, id),
          )
          return {
            label: `${name} (${dishCount.count})${
              !isNil(curationDate)
                ? ` - ${formatDateUk(curationDate.curatedAt)}`
                : ''
            }`,
            value: id,
          }
        },
      ),
    ],
    [availableForQaCounts, dishCurationDates, usersData],
  )

  const selectedValue = useMemo(
    () => (!isNil(selectedCurator) ? toString(selectedCurator) : null),
    [selectedCurator],
  )

  return (
    <Select
      allowClear
      showSearch
      options={usersOptions}
      value={selectedValue}
      onChange={onChange}
      disabled={isDisabled}
    />
  )
}

MenuCurationTaskCuratorFilter.propTypes = {
  menuId: PropTypes.number,
  selectedCurator: PropTypes.number,
  onChange: PropTypes.func,
  isDisabled: PropTypes.bool,
  wasDoneInPreviousTwoWeeks: PropTypes.bool,
}

MenuCurationTaskCuratorFilter.defaultProps = {
  menuId: undefined,
  selectedCurator: undefined,
  onChange: noop,
  isDisabled: false,
  wasDoneInPreviousTwoWeeks: false,
}

export default MenuCurationTaskCuratorFilter
